import React, { useState, useEffect } from "react";
import {
  Box, CssBaseline, ThemeProvider, createTheme,
  GlobalStyles,
  Backdrop
} from "@mui/material";

// Components
import AppBarTop from "./components/AppBar";
import Sidebar from "./components/SideBar";
import CustomerRegistration from "./components/CustomerRegistration";
import CustomerDetails from "./components/CustomerDetails";
import Dashboard from "./components/Dashboard";
import Footer from "./components/Footer";
import LoginPage from "./pages/LoginPage";
import SystemUserManagement from "./components/SystemUserManagement";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Swal from "sweetalert2";
import CircularProgress from "@mui/material/CircularProgress";

import {
  getAllCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  uploadCustomerFile,
  BASE_URL,
  customerEnquiry,
  getDashboardData,
  getYourCustomers
} from "./services/customerApi";
import CustomerLeadModule from "./components/CustomerLeadModule";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from "react-router-dom";

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: "#000000ff",
      contrastText: "#000000",
    },
    secondary: {
      main: "#FDB813",
      contrastText: "#111111",
    },
    background: {
      default: "#0a0a0a",
      paper: "#111111",
    },
    text: {
      primary: "#ffffff",
      secondary: "#b0b0b0",
    }
  },
  typography: {
    fontFamily: '"Outfit", "Inter", sans-serif',
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 4, textTransform: 'none', fontWeight: 600 },
      },
    },
  },
});

const initialFormData = {
  id: null,
  name: "",
  phone: "",
  location: "",
  email: "",
  vehicles: [{ type: "two_wheeler", number: "", endDate: "", scheduleDate: "" }],
  endDate: "",
  scheduleDate: "",
  remarks: "",
  files: [],
  deletedFiles: [],
  rating: 0,
  verification: "No",
  verifiedBy: "",
};

// Maps URL paths ↔ internal section keys
const PATH_TO_SECTION = {
  "/dashboard": "dashboard",
  "/enquiry": "users",
  "/lead": "LeadModule",
  "/registration": "registration",
  "/user-creation": "user-creation",
  "/user-details": "user-details",
  "/profile": "profile",
};
const SECTION_TO_PATH = Object.fromEntries(
  Object.entries(PATH_TO_SECTION).map(([k, v]) => [v, k])
);

// Inner component that can use router hooks
function AppInner() {
  const navigate = useNavigate();
  const location = useLocation();

  // Derive section from URL
  const activeSection = PATH_TO_SECTION[location.pathname] || "dashboard";
  const setActiveSection = (section) => navigate(SECTION_TO_PATH[section] || "/dashboard");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editSource, setEditSource] = useState(null);
  const [users, setUsers] = useState([]);
  const [enqUsers, setEnqUsers] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loggedUserType, setLoggedUserType] = useState(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [userName, setUserName] = useState(sessionStorage.getItem("userName") || "");
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });
  const [backDrop, setBackDrop] = useState(false);
  const [filteredEnqUsers, setFilteredEnqUsers] = useState([]);
  const [enquirySearchPhone, setEnquirySearchPhone] = useState("");
  const [expiryFilter, setExpiryFilter] = useState("30");
  const [dashboardData, setDashboardData] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  // Initialize auth from sessionStorage
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const role = sessionStorage.getItem("userRole");
    const savedName = sessionStorage.getItem("userName");
    if (new Date().getTime() > JSON.parse(sessionStorage.getItem("sessionActiveTime"))) {
      sessionStorage.clear();
      setIsLoggedIn(false);
      return;
    }
    if (token) {
      setIsLoggedIn(true);
      setLoggedUserType(role);
      setUserName(savedName);
    }
  }, []);

  // ── Reset all module-level filter / navigation state ─────────────────────
  const resetModuleState = () => {
    setActiveTab(0);
    setSearchTerm("");
    setSearchColumn("all");
    setStatusFilter("all");
    setPage(0);
    setExpiryFilter("30");
    setUsers([]);
    setEnqUsers([]);
    setFilteredEnqUsers([]);
    setEnquirySearchPhone("");
    setDashboardData({});
    setFormData(initialFormData);
    setIsEditing(false);
    setActiveStep(0);
  };

  const handleLoginSuccess = (role) => {
    resetModuleState();   // <-- clear any stale state from the previous session
    sessionStorage.setItem("lastLoginTime", new Date().toISOString());
    const userRole = role || sessionStorage.getItem("userRole");
    setLoggedUserType(userRole);
    setIsLoggedIn(true);
    fetchDashboardData();
    navigate(userRole === "MARKETER" ? "/enquiry" : "/dashboard");
  };

  const handleLogout = () => {
    resetModuleState();   // <-- clear state before wiping the session
    sessionStorage.clear();
    setIsLoggedIn(false);
    setLoggedUserType(null);
    setUserName("");
    navigate("/login");
  };

  const toggleDrawer = () => setDrawerOpen(prev => !prev);

  // Scroll + session check on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (new Date().getTime() > JSON.parse(sessionStorage.getItem("sessionActiveTime"))) {
      sessionStorage.clear();
      setIsLoggedIn(false);
    }
    if (location.pathname === "/enquiry") {
      setFilteredEnqUsers([]);
      setEnquirySearchPhone("");
    }
  }, [location.pathname]);

  // Side effects based on section
  useEffect(() => {
    if (activeSection === "registration" && !isEditing) {
      setFormData(initialFormData);
      setActiveStep(0);
    }
    if (activeSection === "dashboard") fetchDashboardData();
    if (activeSection === "LeadModule") {
      fetchEnqCustomers(activeTab === 0 ? (expiryFilter || "30") : "All");
    }
  }, [activeSection, isEditing, activeTab]);

  const fetchDashboardData = async () => {
    try {
      setBackDrop(true);
      const data = await getDashboardData();
      setDashboardData(data);
    } catch (err) {
      if (!sessionStorage.getItem("token")) { sessionStorage.clear(); setIsLoggedIn(false); }
      else console.error("Error loading dashboard", err);
    } finally { setBackDrop(false); }
  };

  const fetchCustomers = async () => {
    try {
      setBackDrop(true);
      const data = await getAllCustomers('All');
      setUsers(data);
    } catch (err) {
      if (!sessionStorage.getItem("token")) { sessionStorage.clear(); setIsLoggedIn(false); }
      else console.error("Error loading customers", err);
    } finally { setBackDrop(false); }
  };

  const fetchEnqCustomers = async (ind) => {
    try {
      setBackDrop(true);
      const data = activeTab === 0
        ? await getAllCustomers(ind || expiryFilter)
        : await getYourCustomers(sessionStorage.getItem("userId"));
      setEnqUsers(data);
    } catch (err) {
      setEnqUsers([]);
      if (!sessionStorage.getItem("token")) { sessionStorage.clear(); setIsLoggedIn(false); }
      else console.error("Error loading customers", err);
    } finally { setBackDrop(false); }
  };

  const handleOpenRegistration = () => {
    setIsEditing(false);
    setFormData(initialFormData);
    setActiveStep(0);
    navigate("/registration");
  };

  const handleExpiryFilter = async (customerDet) => {
    setExpiryFilter(customerDet);
    try {
      setBackDrop(true);
      const data = await getAllCustomers(customerDet);
      setEnqUsers(data);
      Swal.fire({ icon: "success", title: "Filter Applied", text: `Showing customers expiring in ${customerDet} days.`, timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch (err) { console.error("Error loading filtered customers", err); }
    finally { setBackDrop(false); }
  };

  const handleSubmitUser = async (userData, files = []) => {
    try {
      let savedCustomer;
      setBackDrop(true);
      if (userData.id) {
        savedCustomer = await updateCustomer(userData.id, userData);
      } else {
        const currentName = userName || sessionStorage.getItem("userName") || "Admin";
        savedCustomer = await createCustomer({ ...userData, createdBy: currentName });
      }
      let finalCustomer = savedCustomer;
      for (const file of files) {
        if (file instanceof File) finalCustomer = await uploadCustomerFile(savedCustomer.id, file);
      }
      setBackDrop(false);
      setUsers(prev => {
        const exists = prev.some(u => u.id === finalCustomer.id);
        return exists ? prev.map(u => u.id === finalCustomer.id ? finalCustomer : u) : [finalCustomer, ...prev];
      });
      if (!userData.id) {
        setEnquirySearchPhone(userData.phone);
        navigate("/enquiry");
      } else {
        navigate(SECTION_TO_PATH[editSource] || "/enquiry");
      }
      await Swal.fire({ icon: "success", title: "Awesome!", text: "Customer Saved Successfully", confirmButtonColor: "#4CAF50" });
    } catch (err) {
      console.error("Upload failed", err);
      setSnackbar({ open: true, message: "Error while saving the customer", severity: "error" });
      setBackDrop(false);
    }
  };

  const handleEditUser = (user) => {
    setEditSource(activeSection);
    const normalizeVehicleType = (type) => {
      if (!type) return "two_wheeler";
      const t = type.toLowerCase().trim().replace(/\s+/g, '_');
      if (t === "car") return "private_car";
      if (t === "bike") return "two_wheeler";
      if (t === "truck") return "goods_vehicle";
      if (t === "passenger_vehicle") return "passengers_vehicle";
      return t;
    };

    const vehicles = Array.isArray(user.vehicles)
      ? user.vehicles.map(v => typeof v === 'string' 
          ? { type: 'two_wheeler', number: v, endDate: "", scheduleDate: "" } 
          : { type: normalizeVehicleType(v.type), number: v.number || "", endDate: v.endDate || "", scheduleDate: v.scheduleDate || "" })
      : [{ type: 'two_wheeler', number: '', endDate: "", scheduleDate: "" }];
    setFormData({
      ...user,
      vehicles,
      files: user.documents ? user.documents.map(doc => ({ name: doc.name, url: doc.url, size: doc.size, type: doc.type })) : [],
      deletedFiles: [],
    });
    setIsEditing(true);
    setActiveStep(0);
    navigate("/registration");
  };

  const handleDeleteUser = async (id) => {
    try { await deleteCustomer(id); } catch (err) { console.error("Delete failed", err); }
  };

  const handleEnquirySearch = async (customerDet) => {
    if (customerDet.name !== "" || customerDet.phone !== "" || customerDet.vehicleNumber !== "") {
      setBackDrop(true);
      setFilteredEnqUsers(await customerEnquiry(customerDet));
      setBackDrop(false);
    }
  };

  const getStatusText = (dateString) => {
    if (!dateString) return "No Date";
    const diffDays = Math.ceil((new Date(dateString) - new Date()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return "Expired";
    if (diffDays <= 15) return "Expiring Soon";
    return "Active";
  };

  const getUserAggregateStatus = (user) => {
    const dates = [...(user.vehicles || []).map(v => v.endDate), user.endDate].filter(Boolean);
    if (!dates.length) return "Active";
    const statuses = dates.map(getStatusText);
    if (statuses.includes("Expired")) return "Expired";
    if (statuses.includes("Expiring Soon")) return "Expiring Soon";
    return "Active";
  };

  const expiringSoonCount = users.filter(u => getUserAggregateStatus(u) === "Expiring Soon").length;

  const handleFilePreview = (file) => {
    if (!file?.url) { alert("File URL not found"); return; }
    let url = file.url.startsWith("/api") ? `${BASE_URL}${file.url}` : file.url;
    try { window.open(url, "_blank"); } catch (err) { alert("Error opening file."); }
  };

  const removeFile = (index) => {
    const fileToDelete = formData.files[index].url.split(".com/")[1];
    setFormData(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== index), deletedFiles: [...prev.deletedFiles, fileToDelete] }));
  };

  const getUserStatus = (user) => getUserAggregateStatus(user);

  const filterUsers = (list) => list.filter(user => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    if (searchColumn === "all") return (
      user.name?.toLowerCase().includes(term) ||
      user.phone?.includes(term) ||
      user.location?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.createdBy?.toLowerCase().includes(term) ||
      user.vehicles?.some(v => v.number?.toLowerCase().includes(term))
    );
    if (searchColumn === "vehicleNumber") return user.vehicles?.some(v => v.number?.toLowerCase().includes(term));
    return user[searchColumn]?.toLowerCase().includes(term);
  });

  const filteredUsers = filterUsers(users);
  const filteredEnqCustomers = filterUsers(enqUsers);
  const currentUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const commonCustomerProps = {
    users, currentUsers, searchTerm, setSearchTerm, page, setPage, rowsPerPage, filteredUsers,
    handleEditUser, handleDeleteUser, handleFilePreview, searchColumn, setSearchColumn,
    statusFilter, setStatusFilter, getUserStatus, setActiveSection, handleOpenRegistration,
    loggedUserType, handleEnquirySearch, activeSection,
  };

  if (!isLoggedIn) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage onLoginSuccess={handleLoginSuccess} setUserName={setUserName} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <AppBarTop drawerOpen={drawerOpen} toggleDrawer={toggleDrawer} setActiveSection={setActiveSection} handleOpenRegistration={handleOpenRegistration} setStatusFilter={setStatusFilter} userName={userName} expiringSoonCount={expiringSoonCount} />
      <Sidebar drawerOpen={drawerOpen} toggleDrawer={toggleDrawer} activeSection={activeSection} setActiveSection={setActiveSection} handleOpenRegistration={handleOpenRegistration} onLogout={handleLogout} userRole={loggedUserType} />

      <Box sx={{ 
        flex: 1, 
        display: "flex", 
        flexDirection: "column", 
        mt: { xs: 8.75, md: 11.25 }, 
        backgroundColor: "#0a0a0a", 
        minHeight: "calc(100vh - 72px)",
        filter: drawerOpen ? "blur(8px)" : "none",
        transition: "filter 0.3s ease",
        pointerEvents: drawerOpen ? "none" : "auto"
      }}>
        <Box sx={{ flex: 1, p: 0 }}>
          <Routes>
            <Route path="/dashboard" element={<Dashboard totalUsers={dashboardData?.totalCustomer || 0} setActiveSection={setActiveSection} handleOpenRegistration={handleOpenRegistration} setStatusFilter={setStatusFilter} users={users} dashboardData={dashboardData} />} />
            <Route path="/enquiry" element={<CustomerDetails {...commonCustomerProps} filteredEnqUsers={filteredEnqUsers} enquirySearchPhone={enquirySearchPhone} setEnquirySearchPhone={setEnquirySearchPhone} />} />
            <Route path="/lead" element={<CustomerLeadModule {...commonCustomerProps} filteredEnqUsers={filteredEnqCustomers} expiryFilter={expiryFilter} setExpiryFilter={handleExpiryFilter} activeTab={activeTab} setActiveTab={setActiveTab} onRefreshCustomers={() => fetchEnqCustomers(activeTab === 0 ? (expiryFilter || "30") : "All")} />} />
            <Route path="/registration" element={
              <CustomerRegistration
                formData={formData} setFormData={setFormData} removeFile={removeFile}
                isEditMode={isEditing} editSource={editSource} activeStep={activeStep} setActiveStep={setActiveStep}
                onSubmitUser={handleSubmitUser}
                onCancel={() => { setIsEditing(false); setFormData(initialFormData); navigate(SECTION_TO_PATH[editSource] || "/enquiry"); }}
              />
            } />
            <Route path="/user-creation" element={<SystemUserManagement section="user-creation" />} />
            <Route path="/user-details" element={<SystemUserManagement section="user-details" />} />
            <Route path="/profile" element={<SystemUserManagement section="profile" />} />
            <Route path="/" element={<Navigate to={loggedUserType === "MARKETER" ? "/enquiry" : "/dashboard"} replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Box>
        <Footer />
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Backdrop open={backDrop} sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={{ body: { margin: 0, padding: 0, backgroundColor: "#0a0a0a", color: "#ffffff", overflowX: "hidden" }, "*": { boxSizing: "border-box" } }} />
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
